import $ from "jquery";
import i18n from "i18next";
import { getToken, getOrganizationId, getLoginBaseUrl, getUserId } from "./environment_config";
import { deleteDraft, saveDraftFromState } from "./localDraftStore.es6";
import { apiAjax, apiFetch, apiGet, apiPost, apiPatch, apiDelete } from "./http_service";
import { waitForTtsJob } from "./tts_job_poll";
import { waitForJob } from "./job_poll";
import {
  buildBaseUpdatedAtField,
  updateBaseUpdatedAtFromResponse,
  parseSaveConflict,
} from "./saveConflict";
import { withSidecarSuffix } from "./utils.es6";
import { isFileTooLarge, maxUploadMbFor } from "./upload_limits.es6";
import { repairFileNames } from "./file_name_utils.es6";
import { isBuildLocked } from "../_editor/components/ai-chat/buildMode";

const getAuthToken = () => getToken();

// Visor-reachable calls: anonymous viewers have no token, and "Bearer null"
// makes the server answer 401 instead of treating them as anonymous.
const optionalAuthHeader = () => {
  const token = getAuthToken();
  return token ? { Authorization: "Bearer " + token } : {};
};

export function escapeRegExp(str) {
  return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&");
}

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

// i18next eksik bir anahtarda anahtarın KENDİSİNİ döndürür, bu yüzden düz
// `i18n.t(k) || fallback` kalıbı çalışmaz (ekrana "UploadError.Failed" basar).
function tr(key, fallback) {
  const value = i18n.t(key);
  return !value || value === key ? fallback : value;
}

// Kullanıcıya gösterilecek mesajı üretir: sunucunun JSON gövdesindeki `message`
// alanı varsa onu kullanır, bilinen HTTP durumları için anlaşılır bir metne
// çevirir. 429 = per-user inflight cap (bkz. JobBus.assertInflightCap) — teknik
// metni ("Per-user inflight cap exceeded for upload-media-async") kullanıcıya
// göstermek anlamsız olurdu.
function buildUploadUserMessage({ status, responseBody, fallback }) {
  let serverMessage;
  if (responseBody) {
    try {
      const parsed = JSON.parse(responseBody);
      const raw = parsed && (parsed.message || parsed.error?.message);
      serverMessage = Array.isArray(raw) ? raw[0] : raw;
    } catch (e) {
      // JSON değilse (HTML hata sayfası vb.) sunucu mesajı yok sayılır.
    }
  }

  if (status === 429) {
    return tr(
      "UploadError.TooManyRequests",
      "Aynı anda çok fazla yükleme işlemi var. Devam eden yüklemeler bitince tekrar deneyin."
    );
  }
  if (status === 401 || status === 403) {
    return tr("UploadError.Unauthorized", "Bu işlem için yetkiniz yok. Lütfen tekrar giriş yapın.");
  }
  if (status === 413) {
    return tr("UploadError.TooLarge", "Dosya sunucu limitini aşıyor.");
  }
  if (serverMessage) return serverMessage;
  if (status >= 500) {
    return tr("UploadError.ServerError", "Sunucu hatası nedeniyle dosya yüklenemedi.");
  }
  return fallback || tr("UploadError.Failed", "Dosya yüklenemedi.");
}

export async function _LoadMediaFile(file, callback, percantageCallback, errorCallback) {
  // Report a failure through a dedicated error channel (NOT the progress
  // callback). Diagnostics carry only request/response metadata a user could
  // already read from the browser's dev-tools Network tab — no auth tokens.
  // Guard so a failure is reported at most once, even if both the ajax error
  // handler and the promise rejection fire for the same request.
  let settled = false;
  const reportError = (info) => {
    if (settled) return;
    settled = true;
    if (errorCallback) {
      errorCallback({
        // Her tüketici teknik diagnostics yerine bunu ekrana basabilir.
        userMessage: buildUploadUserMessage({
          status: info && info.status,
          responseBody: info && info.responseBody,
          fallback: info && info.message,
        }),
        time: new Date().toISOString(),
        method: "POST",
        fileName: file && file.name,
        fileSize: file && file.size,
        fileType: file && file.type,
        ...info,
      });
    }
  };

  // Son savunma hattı: sınırı aşan hiçbir dosya sunucuya gönderilmez.
  // Sınır dosya tipine göre değişiyor (video 500 MB, diğerleri 100 MB) —
  // isFileTooLarge/maxUploadMbFor bunu upload_limits.es6'da çözüyor. Bu kontrol
  // tip duyarlı olmazsa büyük bir video, VideoModal'ı geçse bile burada
  // durdurulur ve sunucuya hiç ulaşmaz.
  if (isFileTooLarge(file)) {
    reportError({
      message: `File exceeds the ${maxUploadMbFor(file)}MB upload limit`,
    });
    return false;
  }

  if (!window.ideaLibraryRootUrl) {
    console.warn("ideaLibraryRootUrl not configured");
    reportError({ message: "Upload server is not configured (ideaLibraryRootUrl missing)" });
    return false;
  }

  var formdata01 = new FormData();
  formdata01.append("file", file, file.name);

  // upload-media-async: returns 202 { jobId, room } immediately instead of
  // blocking the request for however long transcode+upload takes (large
  // videos could take minutes on the old sync endpoint, which held the HTTP
  // connection open that whole time). The result is fetched by polling
  // GET api/jobs/{jobId} (see waitForJob below) once the background job
  // reports status:"completed" — its shape mirrors the old sync response
  // (success, fileUrl, fileName, fileSize, fileExtension, mimeType).
  var upload_Url =
    window.ideaLibraryRootUrl +
    "api/ideal-studio/training/upload-media-async/?trainingId=" +
    (window.idealStudioTrainingId || "");

  if (getToken()) {
    formdata01.append("__RequestVerificationToken", getToken());
  }

  let request;
  try {
    request = apiAjax({
      type: "POST",
      url: upload_Url,
      contentType: false,
      processData: false,
      headers: {
        Authorization: "Bearer " + getAuthToken(),
      },
      data: formdata01,
      success: function (ress) {
        // Async endpoint: 202 { jobId, room }. Poll until the background job
        // finishes, then fire the SAME callback(name, type, fileUrl) contract
        // every existing caller already expects — nothing downstream changes.
        if (ress && ress.jobId) {
          if (percantageCallback) {
            percantageCallback("Sunucuda işleniyor...");
          }
          waitForJob(ress.jobId)
            .then((result) => {
              if (result && result.success !== false && result.fileUrl) {
                callback(file.name, file.type, result.fileUrl);
                // Yükleme altyapı job'u — onaylanacak bir sonucu yok; approve
                // edilmezse widget kuyruğunda sonsuza dek sayılıyordu.
                approveJob(ress.jobId);
              } else {
                reportError({
                  endpoint: upload_Url,
                  message:
                    (result && result.message) || "Server reported the upload was not successful",
                });
              }
            })
            .catch((err) => {
              reportError({
                endpoint: upload_Url,
                message: (err && err.message) || "Upload processing failed or timed out",
              });
            });
          return true;
        }
        // Defensive fallback for the old synchronous response shape, in case
        // a rolling deploy briefly has the frontend ahead of the backend.
        if (ress && ress.success) {
          callback(file.name, file.type, ress.fileUrl);
          return true;
        }
        // HTTP 200 but the server reports the upload was not accepted.
        let responseBody;
        try {
          responseBody = ress ? JSON.stringify(ress).slice(0, 500) : undefined;
        } catch (e) {
          responseBody = undefined;
        }
        reportError({
          endpoint: upload_Url,
          status: 200,
          statusText: "OK",
          message: (ress && ress.message) || "Server reported the upload was not successful",
          responseBody,
        });
        return false;
      },
      error: function (jqXHR, textStatus, errorThrown) {
        // eslint-disable-next-line no-console
        console.error("Upload failed:", jqXHR && jqXHR.status, textStatus, errorThrown);
        reportError({
          endpoint: upload_Url,
          status: jqXHR && jqXHR.status,
          statusText: (jqXHR && jqXHR.statusText) || textStatus,
          message: errorThrown || textStatus || "Network or server error",
          responseBody:
            jqXHR && jqXHR.responseText ? String(jqXHR.responseText).slice(0, 500) : undefined,
        });
        return false;
      },
      xhr: function () {
        var xhr = $.ajaxSettings.xhr();
        xhr.upload.onprogress = function (evt) {
          if (percantageCallback) {
            var percentage = Math.floor((evt.loaded / evt.total) * 100);
            percantageCallback("%" + percentage);
            if (percentage >= 100) {
              percantageCallback("Dosya sunucusuna yükleniyor...");
            }
          }
        };
        xhr.upload.onload = function () {};
        return xhr;
      },
    });
  } catch (e) {
    reportError({
      endpoint: upload_Url,
      message: (e && e.message) || "Upload request could not be started",
    });
    return false;
  }

  // apiAjax rejects (without firing the ajax error handler) when the request
  // can't even be sent — e.g. jQuery unavailable on window. Surface it instead
  // of leaving the UI stuck on the spinner.
  if (request && typeof request.catch === "function") {
    request.catch((e) => {
      reportError({
        endpoint: upload_Url,
        message: (e && e.message) || "Upload request could not be sent (server unreachable)",
      });
    });
  }

  return;
}

export async function fetchIdealStudioFiles(params = {}) {
  if (!window.ideaLibraryRootUrl) {
    console.warn("ideaLibraryRootUrl not configured");
    return null;
  }

  const {
    idealStudioTrainingId,
    limit = 20,
    offset = 0,
    orderBy = "created_at",
    orderDirection = "DESC",
    fileType,
    fileExtension,
    provider,
    search,
    navId,
  } = params;

  const trainingId = idealStudioTrainingId || window.idealStudioTrainingId || null;

  if (!trainingId) {
    console.warn("idealStudioTrainingId is not set");
    return null;
  }

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.toLowerCase().startsWith("http")) {
    baseUrl = window.location.origin + baseUrl;
  }

  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  const url = baseUrl + "api/ideal-studio/files";

  const searchParams = new URLSearchParams();
  searchParams.append("idealStudioTrainingId", trainingId);
  searchParams.append("limit", String(limit));
  searchParams.append("offset", String(offset));
  searchParams.append("orderBy", orderBy);
  searchParams.append("orderDirection", orderDirection);

  if (fileType) {
    const types = Array.isArray(fileType) ? fileType : [fileType];
    types.forEach((t) => searchParams.append("fileType", t));
  }
  if (fileExtension) {
    const exts = Array.isArray(fileExtension) ? fileExtension : [fileExtension];
    exts.forEach((e) => searchParams.append("fileExtension", e));
  }
  if (provider) searchParams.append("provider", provider);
  if (search) searchParams.append("search", search);
  if (navId) searchParams.append("navId", navId);

  try {
    const response = await apiGet(`${url}?${searchParams.toString()}`, {
      headers: { Authorization: "Bearer " + getAuthToken() },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch ideal studio files");
    }
    const json = await response.json();
    if (json && Array.isArray(json.files)) json.files = repairFileNames(json.files);
    return json;
  } catch (err) {
    console.error("Failed to fetch ideal studio files", err);
    throw err;
  }
}

export async function fetchOrgFiles(params = {}) {
  const {
    page = 1,
    limit = 20,
    fileType,
    fileExtension,
    provider,
    search,
    orderBy = "createdAt",
    orderDirection = "DESC",
    groupBy,
    trainingId,
    ignoreDefaultTrainingId,
    signal,
  } = params;

  const defaultTrainingId = window.idealStudioTrainingId || null;
  const organizationId = getOrganizationId();

  const searchParams = new URLSearchParams();
  searchParams.append("page", String(page));
  searchParams.append("limit", String(Math.min(limit, 100)));
  searchParams.append("orderBy", orderBy);
  searchParams.append("orderDirection", orderDirection);
  if (groupBy !== undefined) searchParams.append("groupBy", String(groupBy));
  if (organizationId) searchParams.append("organizationId", organizationId);

  // trainingId yönetimi
  if (trainingId !== undefined) {
    // Açıkça trainingId belirtilmişse (null olsa bile) onu kullan
    if (trainingId !== null) {
      console.log("[fetchOrgFiles] Adding trainingId to URL:", trainingId);
      searchParams.append("trainingId", trainingId);
    } else {
      console.log("[fetchOrgFiles] trainingId is null, not adding to URL");
    }
  } else if (!ignoreDefaultTrainingId && defaultTrainingId) {
    // trainingId belirtilmemişse ve ignore flag yoksa window'dan al
    console.log("[fetchOrgFiles] Using default trainingId from window:", defaultTrainingId);
    searchParams.append("trainingId", defaultTrainingId);
  } else {
    console.log("[fetchOrgFiles] No trainingId will be sent", {
      trainingIdParam: trainingId,
      ignoreDefault: ignoreDefaultTrainingId,
      defaultFromWindow: defaultTrainingId,
    });
  }

  if (fileType) {
    const types = Array.isArray(fileType) ? fileType : [fileType];
    types.forEach((t) => searchParams.append("fileType", t));
  }
  if (fileExtension) {
    const exts = Array.isArray(fileExtension) ? fileExtension : [fileExtension];
    exts.forEach((e) => searchParams.append("fileExtension", e));
  }
  if (provider) searchParams.append("provider", provider);
  if (search) searchParams.append("search", search);

  const url = `${process.env.MW_API_URL}api/ideal-studio-files/organization-assets?${searchParams.toString()}`;

  console.log("[fetchOrgFiles] Request URL:", url, {
    trainingId,
    ignoreDefaultTrainingId,
    defaultTrainingId,
    groupBy,
  });

  try {
    const response = await fetch(url, {
      headers: { Authorization: "Bearer " + getAuthToken() },
      signal,
    });
    if (!response.ok) {
      throw new Error("Failed to fetch organization files");
    }
    const json = await response.json();
    if (json && Array.isArray(json.data)) json.data = repairFileNames(json.data);
    return json;
  } catch (err) {
    console.error("Failed to fetch organization files", err);
    throw err;
  }
}

// Provider marker the middleware stamps on generated sound-effect file records.
// "My Effects" (Efektlerim) filters org assets by this exact string.
export const SFX_PROVIDER = "elevenlabs-sfx";

// Generate sound effect(s) via ElevenLabs (POST api/tts/sound-effect).
// `params`: { text, enrich?, systemPrompt?, durationSeconds?, promptInfluence?,
// loop?, modelId?, outputFormat?, count? }. Two modes handled server-side —
// enrich=true runs an LLM to expand the prompt first. Each produced sound is
// persisted org-level so it appears in "My Effects" across every training.
// Returns { success, mode, prompt, originalText, request, sounds:[{id,audioUrl,duration,...}] }.
export async function generateSoundEffect(params) {
  if (!process.env.MW_API_URL) {
    throw new Error("generateSoundEffect: MW_API_URL not configured");
  }
  const url = process.env.MW_API_URL + "api/tts/sound-effect";
  const response = await apiPost(url, params, {
    headers: { Authorization: "Bearer " + getAuthToken() },
  });
  if (!response.ok) {
    throw new Error("sound-effect generation failed: HTTP " + response.status);
  }
  return response.json();
}

// Org-scoped "My Effects" list. Reuses the org-assets endpoint filtered by the
// SFX provider marker; ignoreDefaultTrainingId so org-level effects (no
// training) return regardless of which training is currently open.
export async function fetchOrgSoundEffects({ search, limit = 100, signal } = {}) {
  const result = await fetchOrgFiles({
    provider: SFX_PROVIDER,
    fileType: "audio",
    ignoreDefaultTrainingId: true,
    limit,
    search,
    signal,
  });
  // fetchOrgFiles resolves to { success, data, pagination } (data = flat list).
  return (result && (result.data || result.files)) || [];
}

export async function deleteIdealStudioFile(fileId) {
  if (!fileId) {
    console.warn("deleteIdealStudioFile called without fileId");
    return null;
  }

  const baseUrl = process.env.MW_API_URL || "";
  const url = `${baseUrl}api/ideal-studio/files/${fileId}`;

  try {
    const response = await apiDelete(url, {
      headers: { Authorization: "Bearer " + getAuthToken() },
    });
    if (!response.ok) {
      throw new Error("Failed to delete ideal studio file");
    }
    return await response.json();
  } catch (err) {
    console.error("Failed to delete ideal studio file", err);
    throw err;
  }
}

// Bir görselin reference_id versiyon ailesini getirir (orijinal + türevleri).
// "Görsel Animatörü" modalındaki versiyon sekmesi kullanır.
// GET {MW_API_URL}api/ideal-studio-files/versions?url=<fileVirtualPath>
export async function fetchFileVersions(url) {
  if (!url) return [];

  const baseUrl = process.env.MW_API_URL || "";
  const endpoint = `${baseUrl}api/ideal-studio-files/versions?url=${encodeURIComponent(url)}`;

  try {
    const response = await apiGet(endpoint, {
      headers: { Authorization: "Bearer " + getAuthToken() },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch file versions");
    }
    const data = await response.json();
    return data?.versions || [];
  } catch (err) {
    console.error("Failed to fetch file versions", err);
    return [];
  }
}

// Dosyanın referans (türetildiği) görseli var mı — middleware yalnız
// reference_id'ye bakar, versiyon ailesini çekmez (fetchFileVersions'tan hızlı).
// GET {MW_API_URL}api/ideal-studio-files/has-reference?url=<fileVirtualPath>
// Endpoint henüz deploy edilmemiş bir middleware'de (404) versions'a düşer.
export async function fetchHasReferenceImage(url) {
  if (!url) return false;

  const baseUrl = process.env.MW_API_URL || "";
  const endpoint = `${baseUrl}api/ideal-studio-files/has-reference?url=${encodeURIComponent(url)}`;

  try {
    const response = await apiGet(endpoint, {
      headers: { Authorization: "Bearer " + getAuthToken() },
    });
    if (response.status === 404) {
      const versions = await fetchFileVersions(url);
      return versions.some(
        (v) =>
          v?.fileVirtualPath && !/\.(mp4|webm|mov|m4v|webp|gif)(\?|#|$)/i.test(v.fileVirtualPath)
      );
    }
    if (!response.ok) {
      throw new Error("Failed to fetch has-reference");
    }
    const data = await response.json();
    return !!data?.hasReference;
  } catch (err) {
    console.error("Failed to fetch has-reference", err);
    return false;
  }
}

// Bulk infographic animation — thin wrappers over the middleware's
// /api/animation/bulk/* endpoints, mirroring fetchFileVersions above
// (URL-building, auth header, error handling).
// source: "infographic" (default server-side) | "generated" | "all" — see
// AnimateV2BulkController.preflight in aidea-middleware.
export async function fetchBulkAnimatePreflight(trainingId, source) {
  if (!trainingId) return null;

  const baseUrl = process.env.MW_API_URL || "";
  const query = source ? `?source=${encodeURIComponent(source)}` : "";
  const endpoint = `${baseUrl}api/animation/bulk/${trainingId}/preflight${query}`;

  try {
    const response = await apiGet(endpoint, {
      headers: { Authorization: "Bearer " + getAuthToken() },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch bulk animate preflight");
    }
    return await response.json();
  } catch (err) {
    console.error("Failed to fetch bulk animate preflight", err);
    return null;
  }
}

export async function startBulkAnimate(trainingId, payload) {
  if (!trainingId) return null;

  const baseUrl = process.env.MW_API_URL || "";
  const endpoint = `${baseUrl}api/animation/bulk/${trainingId}/start`;

  try {
    const response = await apiPost(endpoint, payload, {
      headers: { Authorization: "Bearer " + getAuthToken() },
    });
    if (!response.ok) {
      throw new Error("Failed to start bulk animate");
    }
    return await response.json();
  } catch (err) {
    console.error("Failed to start bulk animate", err);
    return null;
  }
}

export async function fetchBulkAnimateActive(trainingId) {
  if (!trainingId) return null;

  const baseUrl = process.env.MW_API_URL || "";
  const endpoint = `${baseUrl}api/animation/bulk/${trainingId}/active`;

  try {
    const response = await apiGet(endpoint, {
      headers: { Authorization: "Bearer " + getAuthToken() },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch bulk animate active batch");
    }
    return await response.json();
  } catch (err) {
    console.error("Failed to fetch bulk animate active batch", err);
    return null;
  }
}

export async function cancelBulkAnimate(batchId) {
  if (!batchId) return null;

  const baseUrl = process.env.MW_API_URL || "";
  const endpoint = `${baseUrl}api/animation/bulk/${batchId}/cancel`;

  try {
    const response = await apiPost(
      endpoint,
      {},
      { headers: { Authorization: "Bearer " + getAuthToken() } }
    );
    if (!response.ok) {
      throw new Error("Failed to cancel bulk animate batch");
    }
    return await response.json();
  } catch (err) {
    console.error("Failed to cancel bulk animate batch", err);
    return null;
  }
}

export async function resumeBulkAnimate(batchId) {
  if (!batchId) return null;

  const baseUrl = process.env.MW_API_URL || "";
  const endpoint = `${baseUrl}api/animation/bulk/${batchId}/resume`;

  try {
    const response = await apiPost(
      endpoint,
      {},
      { headers: { Authorization: "Bearer " + getAuthToken() } }
    );
    if (!response.ok) {
      throw new Error("Failed to resume bulk animate batch");
    }
    return await response.json();
  } catch (err) {
    console.error("Failed to resume bulk animate batch", err);
    return null;
  }
}

export async function resetBulkAnimate(batchId) {
  if (!batchId) return null;

  const baseUrl = process.env.MW_API_URL || "";
  const endpoint = `${baseUrl}api/animation/bulk/${batchId}/reset`;

  try {
    const response = await apiPost(
      endpoint,
      {},
      { headers: { Authorization: "Bearer " + getAuthToken() } }
    );
    if (!response.ok) {
      throw new Error("Failed to reset bulk animate batch");
    }
    return await response.json();
  } catch (err) {
    console.error("Failed to reset bulk animate batch", err);
    return null;
  }
}

export async function ackBulkAnimateApplied(batchId) {
  if (!batchId) return null;

  const baseUrl = process.env.MW_API_URL || "";
  const endpoint = `${baseUrl}api/animation/bulk/${batchId}/applied`;

  try {
    const response = await apiPost(
      endpoint,
      {},
      { headers: { Authorization: "Bearer " + getAuthToken() } }
    );
    if (!response.ok) {
      throw new Error("Failed to ack bulk animate applied");
    }
    return await response.json();
  } catch (err) {
    console.error("Failed to ack bulk animate applied", err);
    return null;
  }
}

// Approve a single job (POST jobs/:id/approve) — used by studio-side apply paths
// (AnimatorPanel per-card Approve, ImageAnimatorModal Accept) that apply a result
// to the canvas without going through the tray, so those jobs don't linger with
// approved=false. Best-effort: never throws, callers must not block the apply.
//
// Kullanıcı route'u; admin/job-management/jobs/approve SuperAdminGuard arkasında
// olduğu için normal kullanıcıda sessizce 403 dönüyordu. Sunucu job'ın çağırana
// ait olduğunu doğrular.
export async function approveJob(jobId) {
  if (!jobId) return null;

  const baseUrl = process.env.MW_API_URL || "";
  const endpoint = `${baseUrl}api/jobs/${encodeURIComponent(jobId)}/approve`;

  try {
    const response = await apiPost(
      endpoint,
      {},
      { headers: { Authorization: "Bearer " + getAuthToken() } }
    );
    if (!response.ok) {
      throw new Error("Failed to approve job");
    }
    return await response.json();
  } catch (err) {
    console.warn("Failed to approve job", jobId, err);
    return null;
  }
}

// TODO STUDIO MIGRATION (Phase 7): middleware /scene-architect/generate,
// /scene-architect/compose, /scene-architect/compose-and-render, and
// /scene-architect/compose-elements now return { jobId, room } instead of
// a synchronous result. Migrate callers of those routes to
// enqueueSceneArchitectGenerate() (defined below) + useJobSocket(jobId, onEvent).
// fetchThemeStyle / fetchThemes / fetchPalettes below are thin reads and
// are NOT queue-driven — no change needed.

export async function fetchThemeStyle(themeId, paletteId, templateId) {
  if (!window.ideaLibraryRootUrl) {
    console.warn("ideaLibraryRootUrl not configured");
    return null;
  }

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.toLowerCase().startsWith("http")) {
    baseUrl = window.location.origin + baseUrl;
  }
  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  const url = `${baseUrl}api/scene-architect/theme-style`;
  const searchParams = new URLSearchParams();
  if (themeId) searchParams.append("themeId", themeId);
  if (paletteId) searchParams.append("paletteId", paletteId);
  if (templateId) searchParams.append("templateId", templateId);

  try {
    const response = await apiGet(`${url}?${searchParams.toString()}`, {
      headers: { Authorization: "Bearer " + getAuthToken() },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch theme HTML");
    }
    const data = await response.json();
    return data;
  } catch (err) {
    console.error("Failed to fetch theme HTML", err);
    throw err;
  }
}

export async function fetchThemes() {
  if (!window.ideaLibraryRootUrl) {
    console.warn("ideaLibraryRootUrl not configured");
    return null;
  }

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.toLowerCase().startsWith("http")) {
    baseUrl = window.location.origin + baseUrl;
  }
  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  // Kullanıcı tarafındaki route (SceneArchitectController) — admin varyantı
  // (/api/admin/scene-architect/themes) SuperAdminGuard arkasında olduğu için
  // editörden normal kullanıcı token'ıyla çağrılamıyordu. İkisi de aynı
  // SceneArchitectService.getThemes() sonucunu döndürür.
  const url = `${baseUrl}api/scene-architect/themes`;

  try {
    const response = await apiGet(url, {
      headers: { Authorization: "Bearer " + getAuthToken() },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch themes");
    }
    return await response.json();
  } catch (err) {
    console.error("Failed to fetch themes", err);
    throw err;
  }
}

export async function fetchPalettes(themeId) {
  if (!window.ideaLibraryRootUrl) {
    console.warn("ideaLibraryRootUrl not configured");
    return null;
  }

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.toLowerCase().startsWith("http")) {
    baseUrl = window.location.origin + baseUrl;
  }
  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  const searchParams = new URLSearchParams();
  if (themeId) searchParams.append("themeId", themeId);
  // themes ile aynı sebep: admin route'u yerine kullanıcı route'u.
  const url = `${baseUrl}api/scene-architect/palettes?${searchParams.toString()}`;

  try {
    const response = await apiGet(url, {
      headers: { Authorization: "Bearer " + getAuthToken() },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch palettes");
    }
    return await response.json();
  } catch (err) {
    console.error("Failed to fetch palettes", err);
    throw err;
  }
}

export async function _LSUploadHtml(trainingId, version, file, callback) {
  return null;

  var formdata01 = new FormData();
  formdata01.append("file", file, file.name);
  formdata01.append("id", trainingId);
  formdata01.append("version", version);

  var upload_Url = window.ideaLibraryRootUrl + "IdealStudioTrainings/UploadLSHtmlPackage";

  if (getToken()) {
    formdata01.append("__RequestVerificationToken", getToken());
  }

  apiAjax({
    type: "POST",
    url: upload_Url,
    contentType: false,
    processData: false,
    data: formdata01,
    success: function (ress) {
      if (ress != null && ress.success && ress.result != null) {
        var _filerootUrl = window.ideaLibraryRootUrl;
        if (_filerootUrl.toLowerCase().indexOf("http") < 0) {
          _filerootUrl = window.location.origin;
        }
        callback(_filerootUrl + ress.result + "/index.html");
        return true;
      }
    },
    error: function (err) {
      console.log("error:::" + err);
      return false;
    },
  });

  return;
}

export async function _LSCreateUrlHtml(trainingId, strState, callback) {
  // The CDN visor bundle has a stable filename (`visor-bundle.js`) that is
  // overwritten on every deploy. We send a cache-busting suffix to the server so
  // the generated index.html forces browsers/CDN edge caches to fetch the freshest
  // copy of the bundle whenever a new deploy or save happens.
  // Format: visor-bundle.js?v=<pkgVersion>&date=<unix-ms>
  const _visorPkgVersion = process.env.VISOR_BUNDLE_VERSION || "";
  const visorVersion = `visor-bundle.js?v=${_visorPkgVersion}&date=${Date.now()}`;

  var _data = {
    trainingId: trainingId,
    visorVersion: visorVersion,
  };

  // if (strState) {
  //   _data.state = strState;
  // }

  var upload_Url = window.ideaLibraryRootUrl + "api/ideal-studio/training/create-html-link";

  const token = getAuthToken();
  try {
    const response = await apiPost(upload_Url, _data, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
    if (response.ok) {
      const ress = await response.json();
      if (ress != null && ress.success) {
        callback(ress.shortenLink);
      }
    }
  } catch (err) {
    console.log("error:::" + err);
  }
}

export async function _LSCheckLmsStatus(trainingId) {
  if (!window.ideaLibraryRootUrl) {
    console.error("_LSCheckLmsStatus: ideaLibraryRootUrl not configured");
    return { isLmsConnect: false, LmsLog: null };
  }

  const _trainingId = trainingId || window.idealStudioTrainingId || "";
  if (!_trainingId) {
    console.error("_LSCheckLmsStatus: trainingId not provided");
    return { isLmsConnect: false, LmsLog: null };
  }

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  const url = baseUrl + "api/lms-logs/" + _trainingId + "/status";
  const token = getAuthToken();

  try {
    const response = await apiGet(url, {
      headers: { Authorization: "Bearer " + token },
    });

    if (!response.ok) {
      console.error("_LSCheckLmsStatus error:", response.status, response.statusText);
      return { isLmsConnect: false, LmsLog: null };
    }

    const data = await response.json();
    return {
      isLmsConnect: data?.isLmsConnect || false,
      LmsLog: data?.LmsLog || null,
    };
  } catch (err) {
    console.error("_LSCheckLmsStatus error:", err);
    return { isLmsConnect: false, LmsLog: null };
  }
}

export async function fetchVoiceProfile(trainingId) {
  if (!window.ideaLibraryRootUrl) {
    console.warn("fetchVoiceProfile: ideaLibraryRootUrl not configured");
    return null;
  }

  const _trainingId = trainingId || window.idealStudioTrainingId || "";
  if (!_trainingId) {
    console.warn("fetchVoiceProfile: trainingId not provided");
    return null;
  }

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  const url = baseUrl + "api/studio/training/" + _trainingId + "/voice-profile";
  const token = getAuthToken();

  try {
    const response = await apiGet(url, {
      headers: { Authorization: "Bearer " + token },
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (err) {
    console.error("fetchVoiceProfile error:", err);
    return null;
  }
}

export async function _LSPublishToLMS({ idealStudioTrainingId, title, overWrite, zipBlob }) {
  if (!window.ideaLibraryRootUrl) {
    console.error("_LSPublishToLMS: ideaLibraryRootUrl not configured");
    throw new Error("Server configuration missing");
  }

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  const url = baseUrl + "api/lms-logs/publish-to-lms";
  const token = getAuthToken();

  const formData = new FormData();
  formData.append(
    "idealStudioTrainingId",
    idealStudioTrainingId || window.idealStudioTrainingId || ""
  );
  formData.append("Title", title || "");
  formData.append("overWrite", overWrite ? "true" : "false");
  formData.append("files", zipBlob, "scorm_export.zip");

  const response = await apiFetch(url, {
    method: "POST",
    headers: { Authorization: "Bearer " + token },
    body: formData,
  });

  if (!response.ok) {
    let errorMessage = "LMS publish error";
    try {
      const errorData = await response.json();
      errorMessage = errorData?.message || errorMessage;
    } catch (e) {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return await response.json();
}

// quiet: opt-in, skips the full-screen #_overlay/#_loading save indicator —
// for the background autosave tick only. Every existing caller omits it and
// keeps the overlay, so this is purely additive.
export function ideaEnvSaveState(state, isExit, callback, onError, force = false, quiet = false) {
  // Live Motion Build: the server writes this training until the build ends.
  if (isBuildLocked()) {
    if (onError) onError({ buildLocked: true });
    return;
  }
  if (!quiet) {
    $("#_overlay").show();
    $("#_loading").show();
  }

  let exportedState = {
    present: {
      ...state.undoGroup.present,
      filesUploaded: state.filesUploaded,
      status: state.status,
      everPublished: state.everPublished,
    },
  };

  var stateData = exportedState.present;
  let strState = JSON.stringify(exportedState);
  let fileList = [];

  let index = 0;
  for (let f in stateData.filesUploaded) {
    let file = stateData.filesUploaded[f];

    if (file.url.includes("base64")) {
      let r = new RegExp(escapeRegExp(file.url), "g");
      let name = file.name;
      fileList.push({
        name: name,
        file: file.url.substring(file.url.indexOf("base64,") + 7, file.url.length),
        mimetype: file.mimetype,
      });
      strState = strState.replace(
        r,
        (window.ideaLibraryRootUrl || "") + "ContentFiles/IdealStudioFiles/" + name
      );
    }
    index++;
  }

  if (
    window.abp &&
    window.abp.services &&
    window.abp.services.app &&
    window.abp.services.app.idealStudioTraining
  ) {
    var _idealStudioAppService = window.abp.services.app.idealStudioTraining;
    _idealStudioAppService
      .lSSaveOrUpdateState({
        id: window.idealStudioTrainingId,
        exportedState: strState,
        files: fileList,
      })
      .done(function (e) {
        if (e) {
          if (!quiet) {
            $("#_overlay").hide();
            $("#_loading").hide();
          }

          callback(e);

          if (isExit) {
            _LSExitAndReturnList();
          }
        }
      })
      .always(function () {});
  } else {
    const queryParams = new URLSearchParams(window?.location?.search);
    const isTemplateView = queryParams?.get("template") === "true";

    var _data = {
      id: window.idealStudioTrainingId,
      exportedState: strState,
      files: fileList,
      isTemplate: isTemplateView,
      ...buildBaseUpdatedAtField(force),
    };

    var upload_Url = window.ideaLibraryRootUrl + "api/ideal-studio/training/save-state";

    apiAjax({
      type: "PUT",
      url: upload_Url,
      contentType: "application/json",
      processData: false,
      headers: {
        Authorization: "Bearer " + getAuthToken(),
      },
      data: JSON.stringify(_data),
      dataType: "json",
      success: function (ress) {
        if (!quiet) {
          $("#_overlay").hide();
          $("#_loading").hide();
        }

        if (ress.success) {
          updateBaseUpdatedAtFromResponse(ress);
          deleteDraft(window.idealStudioTrainingId, getUserId());
          callback(ress);

          if (isExit) {
            _LSExitAndReturnList();
          }
        } else {
          console.error("Save state failed:", ress.message);
          saveDraftFromState(window.idealStudioTrainingId, state, getUserId());

          // Show error modal if callback is available
          if (window.showServerModal) {
            window.showServerModal(
              "Hata",
              ress.message || "Eğitim kaydedilirken bir hata oluştu.",
              false
            );
          }

          if (onError) onError(new Error(ress.message || "Save state failed"));
        }
      },
      error: function (err) {
        // Whatever the failure (incl. a 409), keep a local copy of the work.
        saveDraftFromState(window.idealStudioTrainingId, state, getUserId());
        // A real 409 (the middleware rethrows ConflictException before its
        // shared catch can flatten it into a 200 { success: false }) --
        // handled distinctly from every other failure below, and WITHOUT
        // the generic "Hata" modal, so the caller can show the conflict
        // dialog instead.
        let conflictBody = err.responseJSON || null;
        if (!conflictBody && err.responseText) {
          try {
            conflictBody = JSON.parse(err.responseText);
          } catch (e) {
            conflictBody = null;
          }
        }
        const conflict = parseSaveConflict(err.status, conflictBody);
        if (conflict) {
          if (!quiet) {
            $("#_overlay").hide();
            $("#_loading").hide();
          }

          if (onError) {
            const conflictError = new Error(
              (conflictBody && conflictBody.message) || "Save conflict"
            );
            conflictError.isSaveConflict = true;
            conflictError.currentUpdatedAt = conflict.currentUpdatedAt;
            onError(conflictError);
          }
          return;
        }

        console.error("Save state error:", err);
        if (!quiet) {
          $("#_overlay").hide();
          $("#_loading").hide();
        }

        if (window.showServerModal) {
          let errorMessage = "Eğitim kaydedilirken bir hata oluştu.";
          if (err.responseJSON && err.responseJSON.error) {
            errorMessage = err.responseJSON.error.message || errorMessage;
          } else if (err.responseText) {
            try {
              const errorData = JSON.parse(err.responseText);
              errorMessage = errorData.error?.message || errorData.message || errorMessage;
            } catch (e) {
              // If parsing fails, use default message
            }
          } else if (err.statusText === "error" || err.status === 0) {
            // CORS or network error
            errorMessage = "Eğitim kaydedilirken bir hata oluştu.";
          }

          window.showServerModal("Hata", errorMessage, false);
        }

        if (onError) onError(err);
      },
    }).catch(function (err) {
      // Catch any unhandled promise rejection (e.g., CORS errors)
      console.error("Save state unhandled error:", err);
      if (!quiet) {
        $("#_overlay").hide();
        $("#_loading").hide();
      }

      if (window.showServerModal) {
        window.showServerModal("Hata", "Eğitim kaydedilirken bir hata oluştu.", false);
      }

      if (onError) onError(err);
    });
  }

  return;
}

export function _saveAs(name, description, state) {
  $("#_overlay").show();
  $("#_loading").show();

  let exportedState = {
    present: {
      ...state.undoGroup.present,
      filesUploaded: state.filesUploaded,
      status: state.status,
      everPublished: state.everPublished,
    },
  };

  if (
    window.abp &&
    window.abp.services &&
    window.abp.services.app &&
    window.abp.services.app.idealStudioTraining
  ) {
    var _idealStudioAppService = window.abp.services.app.idealStudioTraining;
    _idealStudioAppService
      .createIdealStudioTraining({
        Name: name,
        InformationText: description,
        exportedState: JSON.stringify(exportedState),
      })
      .done(function (e) {
        if (e) {
          $("#_overlay").hide();
          $("#_loading").hide();
          $("#editorModal").modal();
        }
      })
      .always(function () {});
  } else {
    var _data = {
      name: name,
      informationText: description,
      exportedState: JSON.stringify(exportedState),
      createdWithAI: false,
      // Kaynak (kopyalanan) eğitimin id'si — "farklı kaydet" akışında backend
      // hangi eğitimden türetildiğini bunu kullanarak bilir.
      idealStudioTrainingId: window.idealStudioTrainingId || "",
    };

    var upload_Url = window.ideaLibraryRootUrl + "api/ideal-studio/training";

    apiAjax({
      type: "POST",
      url: upload_Url,
      contentType: "application/json",
      processData: false,
      headers: {
        Authorization: "Bearer " + getAuthToken(),
      },
      data: JSON.stringify(_data),
      dataType: "json",
      success: function (ress) {
        $("#_overlay").hide();
        $("#_loading").hide();

        if (ress.success) {
          $("#editorModal").modal();
        } else {
          console.error("Create training failed:", ress.message);

          // Show error modal if callback is available
          if (window.showServerModal) {
            window.showServerModal(
              "Hata",
              ress.message || "Eğitim kaydedilirken bir hata oluştu.",
              false
            );
          }
        }
      },
      error: function (err) {
        console.error("Create training error:", err);
        $("#_overlay").hide();
        $("#_loading").hide();

        // Show error modal if callback is available
        if (window.showServerModal) {
          let errorMessage = "Eğitim kaydedilirken bir hata oluştu.";
          if (err.responseJSON && err.responseJSON.error) {
            errorMessage = err.responseJSON.error.message || errorMessage;
          } else if (err.responseText) {
            try {
              const errorData = JSON.parse(err.responseText);
              errorMessage = errorData.error?.message || errorData.message || errorMessage;
            } catch (e) {
              // If parsing fails, use default message
            }
          }

          window.showServerModal("Hata", errorMessage, false);
        }
      },
    });
  }

  return;
}

export async function _ideaEnvTtsGetVoice(_obj, callback) {
  let { prompt, aiType, langName, voiceName } = _obj;

  $("#_overlay").show();
  $("#_loading").show();

  switch (aiType) {
    case "1":
      aiType = "elevenlabs";
      break;
    case "0":
      aiType = "azure";
      break;
    default:
      aiType = "auto";
  }

  var upload_Url = process.env.MW_API_URL + "api/course/text-to-speech";
  var _data = {
    idealStudioTrainingId: window.idealStudioTrainingId,
    provider: aiType,
    text: prompt,
    language: langName,
    voice: voiceName,
  };

  try {
    // The endpoint is async: 202 { jobId } now, audio later. Keep the overlay
    // up while we poll the job until the worker finishes (or the waiter times
    // out) — hidden in finally.
    const response = await apiPost(upload_Url, _data, {
      headers: {
        Authorization: "Bearer " + getAuthToken(),
      },
    });

    if (!response.ok) {
      callback(false, "System Error");
      return;
    }
    const ress = await response.json();
    const jobId = ress?.jobId;
    if (!jobId) {
      callback(false, "Service Error");
      return;
    }
    const result = await waitForTtsJob(jobId);
    // Tek tüketici ttsGenerateSoundFile sonucu otomatik yükleyip kullanıyor —
    // widget'ta "Onay Bekliyor" kalmasın diye job burada approve edilir.
    if (result?.audioUrl) approveJob(jobId);
    callback(true, "", result.audioUrl, result.provider);
  } catch (err) {
    console.log("_ttsGetVoice fail", err);
    callback(false, "System Error");
  } finally {
    $("#_overlay").hide();
    $("#_loading").hide();
  }
}

export async function _ideaEnvTtiGetImage(_obj, callback) {
  let {
    prompt,
    aiType,
    imgSize,
    imgCount,
    aiQuality,
    aiStyle,
    aiImprovePrompt,
    imagePromptLibraryId,
    // New format parameters
    provider,
    orientation,
    quality,
  } = _obj;
  imgCount = 1;

  $("#_overlay").show();
  $("#_loading").show();

  // If new format parameters are provided, use them directly
  if (provider) {
    // New format - use values as-is
    // provider, orientation, and quality are already in the correct format
  } else {
    // Old format - convert to new format
    switch (aiType) {
      case "1":
        provider = "dalle";
        break;
      case "0":
        provider = "gemini";
        break;
      case "flux":
      case "dalle":
      case "gemini-nano":
      case "auto":
        provider = aiType;
        break;
      case "gemini":
        provider = "gemini-nano";
        break;
      default:
        provider = "auto";
    }

    if (!orientation) {
      orientation = "square";
      if (imgSize === "1792") orientation = "landscape";
      if (imgSize === "17922") orientation = "portrait";
    }

    if (!quality) {
      quality = aiQuality === "1" ? "hd" : "standard";
    }
  }

  let style = aiStyle === "1" ? "natural" : "vivid";

  var upload_Url = window.ideaLibraryRootUrl + "api/ideal-studio/text-to-image";
  var _data = {
    idealStudioTrainingId: window.idealStudioTrainingId,
    provider: provider,
    prompt: prompt,
    imageCount: imgCount,
    orientation: orientation,
    quality: quality,
    style: style,
    improvePrompt: aiImprovePrompt,
    ...(imagePromptLibraryId !== undefined &&
      imagePromptLibraryId !== null && { imagePromptLibraryId: imagePromptLibraryId }),
  };

  try {
    const response = await apiPost(upload_Url, _data, {
      headers: {
        Authorization: "Bearer " + getAuthToken(),
      },
    });
    $("#_overlay").hide();
    $("#_loading").hide();

    if (!response.ok) {
      callback(false, "System Error");
      return;
    }
    const ress = await response.json();
    if (ress.success && ress.images && ress.images.length > 0) {
      callback(true, "", ress.images[0].url, 0, 0);
    } else {
      callback(false, ress.error || "Image generation failed");
    }
  } catch (err) {
    $("#_overlay").hide();
    $("#_loading").hide();
    console.log("_ttsGetImage fail", err);
    callback(false, "System Error");
  }
}

export function _LSExitAndReturnList() {
  // Prevent exit in public preview mode
  if (window.isPublicPreviewMode === true) {
    return;
  }

  $("#_overlay").show();
  $("#_loading").show();

  const returnUrl = `${getLoginBaseUrl()}/courses`;
  /* window.ideaLibraryRootUrl
    ? window.ideaLibraryRootUrl + "IdealStudioTrainings/?rnd=" + Math.random().toString()
    : "/IdealStudioTrainings/?rnd=" + Math.random().toString(); */
  document.location.href = returnUrl;
}

export async function _ideaEnvTtsGetVoiceMeta(_obj, callback) {
  let { soundfileurl } = _obj;

  const defaultMeta = {
    VoiceText: "",
    VoiceServiceType: window.aiSelectedVoiceServiceType == 0 ? "azure" : "OpenAI",
    VoiceLanguage: window.aiSelectedVoiceLang || "tr-TR",
    VoiceName:
      window.aiSelectedVoiceName == "" ? "InTcBOYbBDjVPg15q0rr" : window.aiSelectedVoiceName,
  };

  try {
    const response = await apiGet(withSidecarSuffix(soundfileurl, ".txt"), {
      credentials: "include",
    });
    const data = await response.text();
    if (data.indexOf("{") == 0) {
      callback(true, "", JSON.parse(data));
    } else {
      callback(true, "", defaultMeta);
    }
  } catch (err) {
    callback(true, "", defaultMeta);
  }
}

export async function fetchSubtitleLanguages() {
  const globalConfig = window.IdealStudio?.State?.globalConfig || {};
  const trainingId = window.idealStudioTrainingId || globalConfig.__trainingId || null;
  const ideaLibraryRootUrl = window.ideaLibraryRootUrl || globalConfig.__ideaLibraryRootUrl || null;

  if (!trainingId || !ideaLibraryRootUrl) {
    return null;
  }

  let baseUrl = ideaLibraryRootUrl;
  if (!baseUrl.toLowerCase().startsWith("http")) {
    baseUrl = window.location.origin + baseUrl;
  }
  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  const url = `${baseUrl}api/studio/subtitle/${trainingId}/languages`;

  try {
    const response = await apiGet(url, { headers: optionalAuthHeader() });
    if (!response.ok) {
      throw new Error(`Failed to fetch subtitle languages: ${response.status}`);
    }
    return await response.json();
  } catch (err) {
    console.error("fetchSubtitleLanguages error:", err);
    throw err;
  }
}

export async function fetchAllSubtitlesForLanguage(langCode) {
  const globalConfig = window.IdealStudio?.State?.globalConfig || {};
  const trainingId = window.idealStudioTrainingId || globalConfig.__trainingId || null;
  const ideaLibraryRootUrl = window.ideaLibraryRootUrl || globalConfig.__ideaLibraryRootUrl || null;

  if (!trainingId || !ideaLibraryRootUrl || !langCode) {
    console.warn("Missing required parameters for fetchAllSubtitlesForLanguage");
    return null;
  }

  let baseUrl = ideaLibraryRootUrl;
  if (!baseUrl.toLowerCase().startsWith("http")) {
    baseUrl = window.location.origin + baseUrl;
  }
  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  const url = `${baseUrl}api/studio/subtitle/${trainingId}/all/${langCode}`;

  try {
    const response = await apiGet(url, { headers: optionalAuthHeader() });
    if (!response.ok) {
      throw new Error(`Failed to fetch subtitles for language ${langCode}: ${response.status}`);
    }
    return await response.json();
  } catch (err) {
    console.error(`fetchAllSubtitlesForLanguage error (${langCode}):`, err);
    throw err;
  }
}

/**
 * The signed-in user's profile. With `trainingId` the credit fields describe
 * the organization that PAYS for that training (staff open other orgs'
 * trainings); if that call is refused (or the middleware predates the
 * parameter) the plain profile is returned, so the credit badge never blanks.
 */
export async function fetchUserProfile(trainingId) {
  if (!window.ideaLibraryRootUrl) return null;
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const token = getAuthToken();
  const urls = [];
  if (trainingId) {
    urls.push(`${baseUrl}api/auth/profile?idealStudioTrainingId=${encodeURIComponent(trainingId)}`);
  }
  urls.push(`${baseUrl}api/auth/profile`);
  for (const url of urls) {
    try {
      const response = await apiGet(url, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) continue;
      const json = await response.json();
      return json?.data?.user ?? json?.user ?? null;
    } catch (err) {
      console.error("fetchUserProfile error:", err);
    }
  }
  return null;
}

export async function fetchTrainingShareInfo(trainingId) {
  const _trainingId = trainingId || window.idealStudioTrainingId || "";
  if (!_trainingId || !window.ideaLibraryRootUrl) return null;

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";

  const url = `${baseUrl}api/ideal-studio/training/${_trainingId}/short`;
  const token = getAuthToken();

  try {
    const response = await apiGet(url, { headers: { Authorization: "Bearer " + token } });
    if (!response.ok) return null;
    return await response.json();
  } catch (err) {
    console.error("fetchTrainingShareInfo error:", err);
    return null;
  }
}

export async function patchTrainingShareOpen(trainingId, isOpen) {
  const _trainingId = trainingId || window.idealStudioTrainingId || "";
  if (!_trainingId || !window.ideaLibraryRootUrl) return null;

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";

  const url = `${baseUrl}api/ideal-studio/training/${_trainingId}/share`;
  const token = getAuthToken();

  try {
    const response = await apiPatch(
      url,
      { open: isOpen },
      { headers: { Authorization: "Bearer " + token } }
    );
    if (!response.ok) return null;
    return await response.json();
  } catch (err) {
    console.error("patchTrainingShareOpen error:", err);
    return null;
  }
}

export async function fetchTrainingShareUsers(trainingId) {
  const _trainingId = trainingId || window.idealStudioTrainingId || "";
  if (!_trainingId || !window.ideaLibraryRootUrl) return [];

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";

  const url = `${baseUrl}api/ideal-studio/training/${_trainingId}/share-users`;
  const token = getAuthToken();

  try {
    const response = await apiGet(url, { headers: { Authorization: "Bearer " + token } });
    if (!response.ok) return [];
    return await response.json();
  } catch (err) {
    console.error("fetchTrainingShareUsers error:", err);
    return [];
  }
}

export async function addTrainingShareUser(trainingId, email) {
  const _trainingId = trainingId || window.idealStudioTrainingId || "";
  if (!_trainingId || !window.ideaLibraryRootUrl) return null;

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";

  const url = `${baseUrl}api/ideal-studio/training/${_trainingId}/share-users`;
  const token = getAuthToken();

  try {
    const response = await apiPost(
      url,
      { email },
      { headers: { Authorization: "Bearer " + token } }
    );
    if (!response.ok) return null;
    return await response.json();
  } catch (err) {
    console.error("addTrainingShareUser error:", err);
    return null;
  }
}

// ─── TTS Avatar JobBus API wrappers (Phase 5) ────────────────────────────────
//
// These endpoints now return { jobId, room } (or inline result on fast-path).
// Callers should subscribe via useJobSocket(jobId, onEvent) from
// _editor/utils/jobSocket.js rather than setInterval polling.
//
// TODO STUDIO MIGRATION (Phase 5 prep): middleware now returns { jobId, room } for TTS avatar dispatches. Migrate from setInterval polling to useJobSocket(jobId, onEvent) from _editor/utils/jobSocket.js. See aidea-middleware spec docs/superpowers/specs/2026-05-25-producer-worker-discipline-design.md §7.

/**
 * Enqueue a custom avatar creation from a previously-uploaded video URL.
 *
 * @param {{ organizationId: string, videoUrl: string, timestamp: number }} payload
 * @returns {Promise<{ jobId?: string, room?: string, thumbnailUrl?: string, videoUrl?: string, duration?: number }>}
 *   Fast-path (< 10s): inline { thumbnailUrl, videoUrl, duration }.
 *   Slow-path (timeout): { jobId, room } — subscribe via useJobSocket.
 */
export async function enqueueCustomAvatarCreate(payload) {
  if (!window.ideaLibraryRootUrl) return null;
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const url = `${baseUrl}api/tts-avatar/custom-avatar`;
  const token = getAuthToken();
  try {
    const response = await apiPost(url, payload, {
      headers: { Authorization: "Bearer " + token },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (err) {
    console.error("enqueueCustomAvatarCreate error:", err);
    return null;
  }
}

/**
 * Enqueue an image-to-avatar generation job.
 *
 * @param {FormData} formData - multipart body with `file` + optional params.
 * @returns {Promise<{ jobId: string, room: string, message: string }>}
 */
export async function enqueueGenerateImageAvatar(formData) {
  if (!window.ideaLibraryRootUrl) return null;
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const url = `${baseUrl}api/tts-avatar/generate-image-avatar`;
  const token = getAuthToken();
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: "Bearer " + token },
      body: formData,
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (err) {
    console.error("enqueueGenerateImageAvatar error:", err);
    return null;
  }
}

/**
 * Enqueue a mindmap generation job. Returns { jobId, room }.
 * Caller should subscribe to socket room via useJobSocket(jobId)
 * from _editor/utils/jobSocket.js for the completion event carrying
 * the MindmapResult ({ markdown, markdownWithCitations, citations, htmlPath, htmlUrl }).
 *
 * @param {{ topic: string, assetIds: number[], citationsEnabled?: boolean }} payload
 * @returns {Promise<{ jobId: string, room: string }>}
 */
export async function enqueueMindmapGenerate(payload) {
  if (!window.ideaLibraryRootUrl) {
    console.warn("ideaLibraryRootUrl not configured");
    return null;
  }
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const url = `${baseUrl}api/mindmap/generate`;
  const token = getAuthToken();
  try {
    const response = await apiPost(url, payload, {
      headers: { Authorization: "Bearer " + token },
    });
    if (!response.ok) throw new Error(`enqueueMindmapGenerate: HTTP ${response.status}`);
    return await response.json(); // { jobId, room }
  } catch (err) {
    console.error("enqueueMindmapGenerate error:", err);
    return null;
  }
}

/**
 * Enqueue an interactive scene-architect generation job.
 * Returns { jobId, room } immediately.
 * Caller should subscribe to socket room via useJobSocket(jobId, onEvent)
 * for the completion event carrying the scene result.
 *
 * @param {{ narrationText: string, themeId?: string, paletteId?: string, mode?: 'generate'|'compose'|'compose-and-render'|'compose-elements' }} payload
 * @returns {Promise<{ jobId: string, room: string }>}
 */
export async function enqueueSceneArchitectGenerate(payload) {
  if (!window.ideaLibraryRootUrl) {
    console.warn("enqueueSceneArchitectGenerate: ideaLibraryRootUrl not configured");
    return null;
  }
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const mode = payload.mode || "generate";
  const routeMap = {
    generate: "generate",
    compose: "compose",
    "compose-and-render": "compose-and-render",
    "compose-elements": "compose-elements",
  };
  const route = routeMap[mode] || "generate";
  const url = `${baseUrl}api/scene-architect/${route}`;
  const token = getAuthToken();
  try {
    const response = await apiPost(url, payload, {
      headers: { Authorization: "Bearer " + token },
    });
    if (!response.ok) throw new Error(`enqueueSceneArchitectGenerate: HTTP ${response.status}`);
    return await response.json(); // { jobId, room }
  } catch (err) {
    console.error("enqueueSceneArchitectGenerate error:", err);
    return null;
  }
}

/**
 * Enqueue a magic-element-engine decomposition job via the JobBus path.
 * Returns { jobId, room } immediately.
 * Caller should subscribe to socket room via useJobSocket(jobId, onEvent)
 * for the completion event carrying the LayerSet result.
 *
 * NOTE: The existing polling-based decomposeBoxWithAideaSmartElements() in
 * aideaSmartElements.es6 still works against the /decompose endpoint which
 * returns { jobId } for polling. This function targets the newer
 * /execute endpoint once it is wired up on the middleware side.
 *
 * @param {object} payload - Same shape as DecomposeRequestDto
 * @returns {Promise<{ jobId: string, room: string }>}
 */
export async function enqueueMagicElementExecute(payload) {
  if (!window.ideaLibraryRootUrl) {
    console.warn("enqueueMagicElementExecute: ideaLibraryRootUrl not configured");
    return null;
  }
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const url = `${baseUrl}api/magic-element-engine/execute`;
  const token = getAuthToken();
  try {
    const response = await apiPost(url, payload, {
      headers: { Authorization: "Bearer " + token },
    });
    if (!response.ok) throw new Error(`enqueueMagicElementExecute: HTTP ${response.status}`);
    return await response.json(); // { jobId, room }
  } catch (err) {
    console.error("enqueueMagicElementExecute error:", err);
    return null;
  }
}

export async function deleteTrainingShareUser(trainingId, userId) {
  const _trainingId = trainingId || window.idealStudioTrainingId || "";
  if (!_trainingId || !window.ideaLibraryRootUrl) return null;

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";

  const url = `${baseUrl}api/ideal-studio/training/${_trainingId}/share-users/${userId}`;
  const token = getAuthToken();

  try {
    const response = await apiDelete(url, { headers: { Authorization: "Bearer " + token } });
    if (!response.ok) return null;
    return true;
  } catch (err) {
    console.error("deleteTrainingShareUser error:", err);
    return null;
  }
}

/**
 * Fetch the currently in-flight jobs for the authenticated user.
 * Used by the widget for initial population on load.
 *
 * Phase 9 (2026-05-25): pairs with the WebSocket `job_inflight_changed`
 * event emitted by the middleware's per-user room (`user_<userId>`).
 *
 * TODO: The native async-widget (_editor/components/async-widget/, mounted
 * by JobWidgetHost.jsx — no iframe, no separate repo) should call this on
 * mount to populate initial state, then listen for `job_inflight_changed`
 * socket events to apply live deltas. See _editor/containers/JobWidgetHost.md
 * for the widget's current socket/REST contract.
 */
export async function listInflightJobs() {
  if (!window.ideaLibraryRootUrl) return [];

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.toLowerCase().startsWith("http")) {
    baseUrl = window.location.origin + baseUrl;
  }
  if (!baseUrl.endsWith("/")) baseUrl += "/";

  const url = `${baseUrl}api/jobs/inflight`;
  const token = getAuthToken();

  try {
    const response = await apiGet(url, { headers: { Authorization: "Bearer " + token } });
    if (!response.ok) return [];
    return response.json ? await response.json() : (response.data ?? []);
  } catch (err) {
    console.error("listInflightJobs error:", err);
    return [];
  }
}

// === Task 39 === Enqueue a workflow-architect generation job. Mirrors the
// scene-architect / mindmap helpers above: POST to the middleware which
// returns `{ jobId, room }` immediately. Caller subscribes to the per-job
// socket room (`job_<jobId>`) via useJobSocket to receive progress/completion.
export async function enqueueWorkflowGeneration(trainingId, sectionId, suggestionId) {
  if (!window.ideaLibraryRootUrl) {
    console.warn("enqueueWorkflowGeneration: ideaLibraryRootUrl not configured");
    return null;
  }
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const url = `${baseUrl}api/workflow-architect/generate`;
  const token = getAuthToken();
  try {
    const response = await apiPost(
      url,
      { trainingId, sectionId, suggestionId },
      { headers: { Authorization: "Bearer " + token } }
    );
    if (!response.ok) throw new Error(`enqueueWorkflowGeneration: HTTP ${response.status}`);
    return await response.json(); // { jobId, room }
  } catch (err) {
    console.error("enqueueWorkflowGeneration error:", err);
    throw err;
  }
}
// === /Task 39 ===

// === Task 37 === Load active workflow suggestions for the current training.
// Mirrors the existing share/* helpers above: derive the base URL from
// `window.ideaLibraryRootUrl`, attach the JWT, and parse JSON. The middleware
// endpoint (`GET /api/workflow/suggestions/:trainingId[?sceneId=...]`)
// returns `{ suggestions, navIds, generatedAt }` or `{ suggestions: null, ... }`
// when no active row exists. The thunk treats both as non-fatal — the rail
// renders an empty state rather than an error.
export async function loadWorkflowSuggestions(trainingId, sceneId) {
  const _trainingId = trainingId || window.idealStudioTrainingId || "";
  if (!_trainingId || !window.ideaLibraryRootUrl) return null;

  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";

  const qs = sceneId ? `?sceneId=${encodeURIComponent(sceneId)}` : "";
  const url = `${baseUrl}api/workflow/suggestions/${encodeURIComponent(_trainingId)}${qs}`;
  const token = getAuthToken();

  try {
    const response = await apiGet(url, { headers: { Authorization: "Bearer " + token } });
    if (!response.ok) {
      throw new Error(`loadWorkflowSuggestions: HTTP ${response.status}`);
    }
    return response.json ? await response.json() : (response.data ?? null);
  } catch (err) {
    console.error("loadWorkflowSuggestions error:", err);
    throw err;
  }
}
// === /Task 37 ===

// === Task #2: on-demand suggestion regeneration ===
// POSTs to `/api/workflow/suggestions/regenerate` to enqueue a fresh
// suggestion-generate job. Returns `{ jobId, room }` so the caller can
// subscribe to the per-job socket room for progress / completion.
// Errors propagate to the thunk's catch so the UI surfaces them.
export async function regenerateSuggestions(trainingId, scope, sceneId) {
  const _trainingId = trainingId || window.idealStudioTrainingId || "";
  if (!_trainingId || !window.ideaLibraryRootUrl) {
    throw new Error("regenerateSuggestions: missing trainingId or ideaLibraryRootUrl");
  }
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const url = `${baseUrl}api/workflow/suggestions/regenerate`;
  const token = getAuthToken();
  const response = await apiPost(
    url,
    { trainingId: _trainingId, scope, sceneId },
    { headers: { Authorization: "Bearer " + token } }
  );
  if (!response.ok) {
    throw new Error(`regenerateSuggestions: HTTP ${response.status}`);
  }
  return response.json ? await response.json() : (response.data ?? null);
}
// === /Task #2 ===

// === Workflow Architect — generate from prompt ===
// POST /api/workflow-architect/generate with a user-typed topic + locale, and
// receive a synchronous `{ items, variables, triggers }` payload that the
// caller materializes into the active InteractivePlugin box + section
// workflow. Mirrors enqueueWorkflowGeneration's URL but uses the
// inline-result contract (no jobId) the Architect now exposes for the
// "type a topic, get a workflow" UI added in Round 32.
export async function generateWorkflowFromPrompt(topic, locale) {
  if (!window.ideaLibraryRootUrl) {
    throw new Error("generateWorkflowFromPrompt: missing ideaLibraryRootUrl");
  }
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const url = `${baseUrl}api/workflow-architect/generate`;
  const token = getAuthToken();
  const response = await apiPost(
    url,
    { topic, locale },
    { headers: { Authorization: "Bearer " + token } }
  );
  if (!response.ok) {
    throw new Error(`generateWorkflowFromPrompt: HTTP ${response.status}`);
  }
  return response.json ? await response.json() : (response.data ?? null);
}
// === /Workflow Architect — generate from prompt ===

// === Auto-Configure ===
// POST /api/workflow/auto-configure-item — sends an itemKind + topic prompt to
// the middleware, which runs AutoConfigureItemNode (Azure GPT-4o + per-kind
// Zod validation, 2 self-correct retries) and returns the filled `params`
// shape that drops straight into UPDATE_ITEM_PARAM dispatches in the studio.
// Returns the parsed JSON body verbatim (`{ params }` on success or
// `{ params: null, error }` on failure) so the caller can decide how to
// surface the error in the inspector UI.
export async function autoConfigureItemParams(itemKind, topic, locale) {
  if (!window.ideaLibraryRootUrl) {
    throw new Error("autoConfigureItemParams: missing ideaLibraryRootUrl");
  }
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const url = `${baseUrl}api/workflow/auto-configure-item`;
  const token = getAuthToken();
  const response = await apiPost(
    url,
    { itemKind, topic, locale },
    { headers: { Authorization: "Bearer " + token } }
  );
  if (!response.ok) {
    throw new Error(`autoConfigureItemParams: HTTP ${response.status}`);
  }
  return response.json ? await response.json() : (response.data ?? null);
}
// === /Auto-Configure ===

// === Suggest-Hint ===
// POST /api/workflow/suggest-hint — asks the middleware to write a single
// pedagogical hint for a graded item. The body mirrors the rendered item's
// context: { itemKind, prompt, correctAnswer, options?, locale? }. Returns
// { hint: string } on success or { hint: null, error } on failure. The Studio
// inspector wires the success path into UPDATE_ITEM_PARAM at ["params","hint"]
// so the runtime can render a "?" tooltip near the question prompt.
export async function suggestHint({ itemKind, prompt, correctAnswer, options, locale }) {
  if (!window.ideaLibraryRootUrl) {
    throw new Error("suggestHint: missing ideaLibraryRootUrl");
  }
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const url = `${baseUrl}api/workflow/suggest-hint`;
  const token = getAuthToken();
  const response = await apiPost(
    url,
    { itemKind, prompt, correctAnswer, options, locale },
    { headers: { Authorization: "Bearer " + token } }
  );
  if (!response.ok) {
    throw new Error(`suggestHint: HTTP ${response.status}`);
  }
  return response.json ? await response.json() : (response.data ?? null);
}
// === /Suggest-Hint ===

// === Suggest-Distractors ===
// POST /api/workflow/suggest-distractors — asks the middleware to write 3-4
// plausible-but-wrong option labels for an MCQ / MultipleAnswer item. The body
// mirrors the rendered item's context: { kind, prompt, correctOption, locale? }.
// Returns { distractors: string[] } on success or { distractors: null, error }
// on failure. The Studio inspector wires the success path into
// UPDATE_ITEM_PARAM at ["options"] (appending the new options).
export async function suggestDistractors({ kind, prompt, correctOption, locale }) {
  if (!window.ideaLibraryRootUrl) {
    throw new Error("suggestDistractors: missing ideaLibraryRootUrl");
  }
  let baseUrl = window.ideaLibraryRootUrl;
  if (!baseUrl.endsWith("/")) baseUrl += "/";
  const url = `${baseUrl}api/workflow/suggest-distractors`;
  const token = getAuthToken();
  const response = await apiPost(
    url,
    { kind, prompt, correctOption, locale },
    { headers: { Authorization: "Bearer " + token } }
  );
  if (!response.ok) {
    throw new Error(`suggestDistractors: HTTP ${response.status}`);
  }
  return response.json ? await response.json() : (response.data ?? null);
}
// === /Suggest-Distractors ===

// === Convert Aspect Ratio ===
// "Farklı Oranda Üret" — reproduce the current training at a different aspect
// ratio / resolution as a brand-new training via an async AI job. Mirrors the
// parent web app's POST /api/course/training/convert-aspect-ratio, but Studio
// calls the middleware (MW_API_URL) directly with a Bearer token instead of the
// Next.js proxy. Returns { jobId, newTrainingId }; poll getConvertJobStatus().
export async function convertAspectRatio({
  trainingId,
  targetAspectRatio,
  name,
  resolution = "2K",
  language,
}) {
  const _trainingId = trainingId || window.idealStudioTrainingId || "";
  if (!_trainingId) throw new Error("convertAspectRatio: missing trainingId");
  if (!process.env.MW_API_URL) throw new Error("convertAspectRatio: MW_API_URL not configured");

  const url = process.env.MW_API_URL + "api/course/training/convert-aspect-ratio";
  const body = {
    trainingId: _trainingId,
    targetAspectRatio,
    name,
    resolution,
    ...(language ? { language } : {}),
  };
  const response = await apiPost(url, body, {
    headers: { Authorization: "Bearer " + getAuthToken() },
  });
  if (!response.ok) {
    // Surface the backend's JSON error ({ message, error, statusCode }) so the
    // caller can localize it instead of showing a bare HTTP code.
    let parsed = null;
    try {
      parsed = response.json ? await response.json() : (response.data ?? null);
    } catch {
      parsed = null;
    }
    const err = new Error(
      (parsed && parsed.message) || `convertAspectRatio: HTTP ${response.status}`
    );
    err.statusCode = (parsed && parsed.statusCode) || response.status;
    err.serverMessage = parsed && parsed.message;
    throw err;
  }
  return response.json ? await response.json() : (response.data ?? null);
}

// Poll GET {MW_API_URL}api/jobs/{jobId} once. Returns the raw job payload
// ({ status, imageCount, message, progress, result }). Caller loops on a timer
// until status === "completed" | "failed".
export async function getConvertJobStatus(jobId) {
  if (!jobId) throw new Error("getConvertJobStatus: missing jobId");
  if (!process.env.MW_API_URL) throw new Error("getConvertJobStatus: MW_API_URL not configured");
  const url = process.env.MW_API_URL + "api/jobs/" + jobId;
  const response = await apiGet(url, {
    headers: { Authorization: "Bearer " + getAuthToken() },
  });
  if (!response.ok) throw new Error(`getConvertJobStatus: HTTP ${response.status}`);
  return response.json ? await response.json() : (response.data ?? null);
}
// === /Convert Aspect Ratio ===
